/**
 * Daggerheart PC weapons + rest moves (0.8.52).
 * Attack = Duality on the weapon trait (Atlas tray); damage dice roll separately.
 * Armor slots clear only when the Repair Armor rest move is chosen.
 * Weapon rows sit immediately below the six trait lines.
 */

import {
  TIER_1_PRIMARY_WEAPONS,
  TIER_1_SECONDARY_WEAPONS,
  type SrdWeapon,
} from "./daggerheartSrd2";
import {
  isDaggerheartPC,
  parseCreatureFromNoteText,
  traitModifier,
} from "./diceRoll";

export type PcWeaponSlot = "primary" | "secondary";

export type PcWeapon = {
  name: string;
  damage: string;
  damageType: string;
  type?: string;
  range: string;
  trait: string;
  slot: PcWeaponSlot;
};

export type RestMoveId =
  | "tend-wounds"
  | "clear-stress"
  | "repair-armor"
  | "prepare";

export type RestKind = "short" | "long";

export type RestTrackers = {
  hp: number;
  maxHp: number;
  stress: number;
  maxStress: number;
  armor: number;
  hope: number;
  maxHope: number;
};

export const REST_MOVES: Array<{
  id: RestMoveId;
  label: string;
  detail: string;
}> = [
  { id: "tend-wounds", label: "Tend to Wounds", detail: "Clear Hit Points (short +2, long all)" },
  { id: "clear-stress", label: "Clear Stress", detail: "Clear all marked Stress" },
  { id: "repair-armor", label: "Repair Armor", detail: "Clear all marked Armor slots" },
  { id: "prepare", label: "Prepare", detail: "Ready yourself for the next scene" },
];

export const REST_MOVE_COUNT: Record<RestKind, number> = {
  short: 2,
  long: 3,
};

const ALL_SRD_WEAPONS: SrdWeapon[] = [
  ...TIER_1_PRIMARY_WEAPONS,
  ...TIER_1_SECONDARY_WEAPONS,
].slice().sort((a, b) => b.name.length - a.name.length);

const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---(?:\r?\n|$)/;
const STATBLOCK_FENCE_RE =
  /^[ \t]*(```+|~~~+)\s*statblock[^\n]*\n([\s\S]*?)^[ \t]*(```+|~~~+)\s*$/m;
const WEAPON_LINE_RE =
  /<div class="statblock-item-container">\s*<div class="line[^"]*(?:primary-weapon|secondary-weapon)[\s\S]*?<\/div>\s*<\/div>/gi;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeRe(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function textOf(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return value == null ? "" : String(value).trim();
}

function nameBoundaryRe(name: string): RegExp {
  return new RegExp(`(?:^|[^A-Za-z])${escapeRe(name)}(?:[^A-Za-z]|$)`, "i");
}

export function srdToPcWeapon(weapon: SrdWeapon, slot: PcWeaponSlot): PcWeapon {
  return {
    name: weapon.name,
    damage: weapon.damage,
    damageType: weapon.damageType,
    type: weapon.type,
    range: weapon.range,
    trait: weapon.trait,
    slot,
  };
}

export function formatWeaponNoteValue(weapon: PcWeapon): string {
  const typeRaw = weapon.damageType || weapon.type || "";
  const type =
    typeRaw === "physical" ? "phy" : typeRaw === "magic" ? "mag" : typeRaw;
  const diceType = [weapon.damage, type].filter(Boolean).join(" ");
  return [weapon.name, diceType, weapon.range].filter(Boolean).join(" — ");
}

export function lookupSrdWeapon(text: string): SrdWeapon | null {
  if (!text || !text.trim()) return null;
  for (const weapon of ALL_SRD_WEAPONS) {
    if (nameBoundaryRe(weapon.name).test(text)) return weapon;
  }
  return null;
}

export function findSrdWeaponsInText(text: string): SrdWeapon[] {
  if (!text || !text.trim()) return [];
  const found: SrdWeapon[] = [];
  const seen = new Set<string>();
  for (const weapon of ALL_SRD_WEAPONS) {
    const key = weapon.name.toLowerCase();
    if (seen.has(key)) continue;
    if (!nameBoundaryRe(weapon.name).test(text)) continue;
    seen.add(key);
    found.push(weapon);
  }
  return found;
}

function normalizeDamageType(raw: string): string {
  const lower = raw.toLowerCase();
  if (lower === "physical" || lower === "phy") return "phy";
  if (lower === "magic" || lower === "mag" || lower === "arcane") return "mag";
  if (lower === "phy/mag") return "phy/mag";
  return raw;
}

function defaultTraitForType(damageType: string): string {
  return damageType === "mag" || damageType === "magic" ? "Instinct" : "Agility";
}

export function parseWeaponLine(
  raw: string,
  slot: PcWeaponSlot = "primary",
): PcWeapon | null {
  const text = raw.replace(/\s+/g, " ").trim();
  if (!text || /^unarmed$/i.test(text)) return null;
  const srd = lookupSrdWeapon(text);
  if (srd) return srdToPcWeapon(srd, slot);
  const damageMatch = /\b(\d+)?d\d+(?:\s*[+-]\s*\d+)?\b/i.exec(text);
  const typeMatch = /\b(phy\/mag|physical|magic|arcane|phy|mag)\b/i.exec(text);
  const rangeMatch = /\b(very close|very far|melee|close|far)\b/i.exec(text);
  const name = text.split(/[—(]/)[0]?.replace(/:\s*$/, "").trim() || text;
  if (!name) return null;
  const damage = damageMatch ? damageMatch[0].replace(/\s+/g, "") : "";
  const damageType = typeMatch ? normalizeDamageType(typeMatch[1]!) : "";
  return {
    name,
    damage,
    damageType,
    range: rangeMatch ? titleRange(rangeMatch[1]!) : "",
    trait: defaultTraitForType(damageType),
    slot,
  };
}

function titleRange(range: string): string {
  return range
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

export function collectPcWeapons(
  creature: Record<string, unknown>,
  noteText?: string,
): PcWeapon[] {
  const weapons: PcWeapon[] = [];
  const seen = new Set<string>();
  const push = (weapon: PcWeapon | null) => {
    if (!weapon || !weapon.name) return;
    const key = weapon.name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    weapons.push(weapon);
  };

  push(parseWeaponLine(textOf(creature.primary_weapon), "primary"));
  push(parseWeaponLine(textOf(creature.secondary_weapon), "secondary"));
  if (!weapons.some((w) => w.slot === "primary")) {
    push(parseWeaponLine(textOf(creature.weapon), "primary"));
  }

  if (weapons.length === 0) {
    const hay = [noteText || "", textOf(creature.weapons), textOf(creature.inventory)]
      .filter(Boolean)
      .join("\n");
    const found = findSrdWeaponsInText(hay);
    const primaries = found.filter((weapon) => weapon.category === "primary");
    const secondaries = found.filter((weapon) => weapon.category === "secondary");
    if (primaries[0]) push(srdToPcWeapon(primaries[0], "primary"));
    else if (found[0]) push(srdToPcWeapon(found[0], "primary"));
    if (secondaries[0]) push(srdToPcWeapon(secondaries[0], "secondary"));
    else if (found[1] && found[1] !== primaries[0] && found[1] !== found[0]) {
      push(srdToPcWeapon(found[1], "secondary"));
    }
  }
  return weapons;
}

export function weaponDamageFormula(damage: string, proficiency: number): string {
  const prof = Number.isFinite(proficiency) ? Math.max(1, Math.floor(proficiency)) : 1;
  const trimmed = damage.replace(/\s+/g, "");
  if (!trimmed) return `${prof}d6`;
  if (/^\d+d/i.test(trimmed)) return trimmed;
  if (/^d\d+/i.test(trimmed)) return `${prof}${trimmed}`;
  return trimmed;
}

export function weaponTraitMod(
  creature: Record<string, unknown>,
  trait: string,
): number {
  if (!trait || trait.toLowerCase() === "spellcast") {
    return Math.max(
      traitModifier(creature, "Instinct"),
      traitModifier(creature, "Presence"),
      traitModifier(creature, "Knowledge"),
    );
  }
  return traitModifier(creature, trait);
}

export function weaponAttackHtml(
  weapon: PcWeapon,
  creature: Record<string, unknown>,
): string {
  const trait = weapon.trait || "Agility";
  const mod = weaponTraitMod(creature, trait);
  const shown = mod >= 0 ? `+${mod}` : String(mod);
  const prof = Number(creature.proficiency);
  const formula = weaponDamageFormula(weapon.damage, Number.isFinite(prof) ? prof : 1);
  const label = weapon.slot === "secondary" ? "Secondary Weapon" : "Primary Weapon";
  const cls = weapon.slot === "secondary" ? "secondary-weapon" : "primary-weapon";
  const typeRaw = weapon.damageType || weapon.type || "";
  const type =
    typeRaw === "physical" ? "phy" : typeRaw === "magic" ? "mag" : typeRaw;
  const damageLink = weapon.damage
    ? `<span class="atlas-dice-link dh-adversary-dice-btn" data-dh-roll="dice" data-dh-roll-payload="${escapeHtml(formula)}" role="button" tabindex="0" title="Click to roll damage ${escapeHtml(formula)}">${escapeHtml(weapon.damage)}</span>`
    : "";
  const mid = [damageLink, type].filter(Boolean).join(" ");
  const value = [escapeHtml(weapon.name), mid, escapeHtml(weapon.range)]
    .filter(Boolean)
    .join(" — ");
  const attack = `<span class="atlas-dice-link dh-adversary-dice-btn" data-dh-roll="attr" data-dh-roll-payload="${escapeHtml(trait)}|${mod}" data-dh-pc-weapon="1" role="button" tabindex="0" title="Attack with ${escapeHtml(weapon.name)} (Duality 2d12 ${shown})">Attack</span>`;
  return `<div class="statblock-item-container"><div class="line ${cls}" data-property="${cls}" data-dh-pc-weapon="1"><span class="property-name">${label}</span><span class="property-text">${value}</span> ${attack}</div></div>`;
}

const TRAIT_NAME_RE = /^(agility|strength|finesse|instinct|presence|knowledge)$/i;

function isPcTraitLine(el: Element): boolean {
  const prop = el.getAttribute("data-property") || "";
  if (TRAIT_NAME_RE.test(prop)) return true;
  const cls = typeof el.className === "string" ? el.className : "";
  if (TRAIT_NAME_RE.test(cls.replace(/\s+/g, " "))) return true;
  if (/\b(agility|strength|finesse|instinct|presence|knowledge)\b/i.test(cls)) {
    return true;
  }
  const name = el.querySelector?.(".property-name")?.textContent?.trim() || "";
  return TRAIT_NAME_RE.test(name);
}

function lastTraitAnchor(root: Element): Element | null {
  const lines = [...root.querySelectorAll(".line, [data-property]")];
  let last: Element | null = null;
  for (const el of lines) {
    if (!isPcTraitLine(el)) continue;
    last = el.closest(".statblock-item-container") ?? el;
  }
  return last;
}

function collectWeaponWraps(root: Element): Element[] {
  const seen = new Set<Element>();
  const wraps: Element[] = [];
  root
    .querySelectorAll(
      "[data-dh-pc-weapon='1'], [data-property='primary-weapon'], [data-property='secondary-weapon'], .line.primary-weapon, .line.secondary-weapon",
    )
    .forEach((el) => {
      const wrap = el.closest(".statblock-item-container") ?? el;
      if (seen.has(wrap)) return;
      seen.add(wrap);
      wraps.push(wrap);
    });
  return wraps;
}

function insertAfterLastTrait(root: Element, html: string): void {
  const column =
    root.querySelector(".column") ||
    root.querySelector(".statblock-content") ||
    root;
  const anchor = lastTraitAnchor(root);
  if (anchor?.parentNode) {
    anchor.insertAdjacentHTML("afterend", html);
    return;
  }
  column.insertAdjacentHTML("beforeend", html);
}

function stripWeaponPropertyLines(html: string): string {
  return html.replace(WEAPON_LINE_RE, "");
}

function insertWeaponRowsAfterTraits(html: string, rows: string): string {
  const match = [...html.matchAll(
    /<div class="statblock-item-container[^"]*"[^>]*>\s*<div class="line[^"]*\b(?:agility|strength|finesse|instinct|presence|knowledge)\b[\s\S]*?<\/div>\s*<\/div>/gi,
  )].pop();
  if (match && match.index != null) {
    const end = match.index + match[0].length;
    return `${html.slice(0, end)}${rows}${html.slice(end)}`;
  }
  const closes = "</div></div></div>";
  const idx = html.lastIndexOf(closes);
  if (idx >= 0) return `${html.slice(0, idx)}${rows}${html.slice(idx)}`;
  return `${html}${rows}`;
}

/** Put (or move) PC weapon Attack rows immediately below the last trait line. */
export function ensureWeaponRowsInFsbHtml(
  html: string,
  creature: Record<string, unknown>,
  noteText?: string,
): string {
  if (!html || !html.includes("<")) return html;
  const weapons = collectPcWeapons(creature, noteText);
  try {
    if (typeof DOMParser !== "undefined") {
      const doc = new DOMParser().parseFromString(
        `<div id="fsb-root">${html}</div>`,
        "text/html",
      );
      const root = doc.getElementById("fsb-root");
      if (root) {
        const existing = collectWeaponWraps(root);
        const rows =
          existing.length > 0
            ? existing.map((el) => el.outerHTML).join("")
            : weapons.map((weapon) => weaponAttackHtml(weapon, creature)).join("");
        if (!rows) return html;
        existing.forEach((el) => el.remove());
        if (!existing.length) {
          root
            .querySelectorAll(
              "[data-property='primary-weapon'], [data-property='secondary-weapon'], .line.primary-weapon, .line.secondary-weapon",
            )
            .forEach((el) => {
              (el.closest(".statblock-item-container") ?? el).remove();
            });
        }
        insertAfterLastTrait(root, rows);
        return root.innerHTML.trim();
      }
    }
  } catch {
    /* fall through */
  }
  if (weapons.length === 0) return html;
  const rows = weapons.map((weapon) => weaponAttackHtml(weapon, creature)).join("");
  return insertWeaponRowsAfterTraits(stripWeaponPropertyLines(html), rows);
}

export function insertWeaponRowsAfterTraitsInDom(
  container: HTMLElement,
  rowsHtml: string,
): void {
  const existing = collectWeaponWraps(container);
  const html =
    existing.length > 0
      ? existing.map((el) => el.outerHTML).join("")
      : rowsHtml;
  if (!html) return;
  existing.forEach((el) => el.remove());
  insertAfterLastTrait(container, html);
}

function quoteYaml(value: string): string {
  return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

function upsertYamlMap(body: string, key: string, value: string): string {
  const re = new RegExp(`^${escapeRe(key)}\\s*:.*$`, "m");
  const line = `${key}: ${quoteYaml(value)}`;
  if (re.test(body)) return body.replace(re, line);
  const trimmed = body.replace(/\s*$/, "");
  return `${trimmed}\n${line}\n`;
}

function upsertNoteField(markdown: string, key: string, value: string): string {
  let out = markdown;
  if (FRONTMATTER_RE.test(out)) {
    out = out.replace(FRONTMATTER_RE, (_full, body: string) => {
      return `---\n${upsertYamlMap(body, key, value)}---\n`;
    });
  }
  if (STATBLOCK_FENCE_RE.test(out)) {
    out = out.replace(
      STATBLOCK_FENCE_RE,
      (_full, open: string, body: string, close: string) => {
        return `${open}statblock\n${upsertYamlMap(body, key, value)}${close}`;
      },
    );
  }
  return out;
}

export function backfillNoteWeapons(markdown: string): {
  text: string;
  changed: boolean;
} {
  if (!markdown || !markdown.trim()) return { text: markdown, changed: false };
  const creature = parseCreatureFromNoteText(markdown);
  if (!creature || !isDaggerheartPC(creature)) {
    return { text: markdown, changed: false };
  }
  const weapons = collectPcWeapons(creature, markdown);
  if (weapons.length === 0) return { text: markdown, changed: false };
  const primary = weapons.find((w) => w.slot === "primary") ?? weapons[0]!;
  const secondary = weapons.find((w) => w.slot === "secondary");
  let next = upsertNoteField(markdown, "primary_weapon", formatWeaponNoteValue(primary));
  if (secondary) {
    next = upsertNoteField(next, "secondary_weapon", formatWeaponNoteValue(secondary));
  }
  return { text: next, changed: next !== markdown };
}

export function applyRestMoves(
  state: RestTrackers,
  moves: RestMoveId[],
  kind: RestKind,
): RestTrackers {
  const next: RestTrackers = { ...state };
  for (const id of moves) {
    switch (id) {
      case "tend-wounds":
        next.hp =
          kind === "long" ? next.maxHp : Math.min(next.maxHp, next.hp + 2);
        break;
      case "clear-stress":
        next.stress = 0;
        break;
      case "repair-armor":
        next.armor = 0;
        break;
      case "prepare":
        break;
      default: {
        const _exhaustive: never = id;
        void _exhaustive;
      }
    }
  }
  if (kind === "long") next.hope = next.maxHope;
  return next;
}
